/*
 * File: ClinicalPreferences.ts
 * Project: mp-mobile-app
 *
 * Created by Cole on 06/14/2021
 *
 * Copyright (c) 2021 MacPractice, Inc. All rights reserved.
 *
 * Description: Stores the clinical summary widget preference.
 */
import {
  applySnapshot,
  flow,
  Instance,
  SnapshotOut,
  types
} from "mobx-state-tree";
import { ClinicalPreferenceValue } from "./ClinicalPreferenceValue";
import { PreferenceBase } from "./PreferenceBase";

export function sortedClinicalPreferences(
  prefs: Instance<typeof ClinicalPreferenceValue>[]
): Instance<typeof ClinicalPreferenceValue>[] {
  return prefs.slice().sort((a, b) => {
    return a.columnNumber === b.columnNumber
      ? a.orderNumber - b.orderNumber
      : a.columnNumber - b.columnNumber;
  });
}

const ClinicalPreferencesInner = types
  .model("ClinicalPreferencesInner", {
    preferences: types.array(ClinicalPreferenceValue),
    type: types.maybeNull(
      types.union(
        types.literal("web"),
        types.literal("desktop"),
        types.literal("default")
      )
    )
  })
  .views(self => ({
    get sortedPreferences() {
      return sortedClinicalPreferences(self.preferences);
    }
  }));

export const ClinicalPreferences = types
  .compose(
    "ClinicalPreferences",
    PreferenceBase,
    types.model("_ClinicalPreferences", {
      value: types.optional(ClinicalPreferencesInner, {
        preferences: [],
        type: null
      })
    })
  )
  .actions(self => ({
    /**
     * Returns the value that should be sent to the server representing this preferences update. This method does not update the preference with the new value.
     * @param key The database pkey this preference represents.
     * @param value The new value of the preference.
     * @returns The value representing this update to be sent to the server.
     */
    valueForUpdate(
      key: string,
      value: SnapshotOut<typeof ClinicalPreferenceValue>[]
    ) {
      return value;
    },
    /**
     * Applies a loaded preference value from the database to this preference object. If the value needs to have some work done to it before storing it (e.g. fetching models from IDs) that happens in this method.
     * @param value The new value from the server.
     */
    // disable lint to keep the interface the same
    // eslint-disable-next-line require-yield
    applyLoadedPrefValue: flow(function*(value) {
      applySnapshot(self.value, value);
    })
  }));
